/**
 * Which warranty a registration's warranty record page shows.
 *
 * Shared by the registration and claim pages, so every page resolves the same
 * record for the same registration.
 */

import type { TFunction } from "i18next";
import type {
  Holding,
  HoldingWarranty,
  PendingWarrantySummary,
  RegistrationCoverage,
} from "@/admin/types/products";
import { manufacturerWarrantyRecord } from "@/admin/lib/manufacturer-warranty";

export type WarrantyRecordSource = HoldingWarranty | PendingWarrantySummary;

/**
 * The plan a draft is waiting on. For any other registration the pending
 * summary is the default plan's guess, not something it will get, so it is not
 * shown. Payloads without ``coverage`` keep the summary.
 */
export function plannedWarranty<T>(holding: {
  coverage?: { state: string } | null;
  pending_warranty_summary?: T | null;
}): T | null {
  if (holding.coverage && holding.coverage.state !== "pending") return null;
  return holding.pending_warranty_summary ?? null;
}

/** Why a registration has no warranty, or null when it has or will get one. */
export function coverageHint(
  coverage: RegistrationCoverage | null | undefined,
  t: TFunction,
): string | null {
  if (!coverage) return null;
  const reason = coverage.reason
    ? t(
        `registrations.coverageReason.${coverage.reason}`,
        coverage.reason.replace(/_/g, " "),
      )
    : "";
  if (coverage.state === "no_warranty") {
    return t(
      "registrations.coverageHint.noWarranty",
      "No {{warrantyNounLower}} on purpose: {{reason}}.",
      { reason },
    );
  }
  if (coverage.state !== "not_issued") return null;
  return reason
    ? t(
        "registrations.coverageHint.notIssuedBecause",
        "No {{warrantyNounLower}} issued: {{reason}}.",
        { reason },
      )
    : t(
        "registrations.coverageHint.notIssued",
        "No {{warrantyNounLower}} has been issued for this registration.",
      );
}

export function isIssuedWarranty(
  warranty: WarrantyRecordSource,
): warranty is HoldingWarranty {
  return "uuid" in warranty;
}

export function findHoldingWarranty(
  holding: Holding | undefined,
  warrantyUuid: string | undefined,
): WarrantyRecordSource | null {
  if (!holding || !warrantyUuid) return null;
  const firstIssued = holding.warranties?.[0] ?? null;
  const planned = plannedWarranty(holding);
  // A saved ``pending-warranty`` link with nothing planned opens the
  // registration's own record instead of a guessed plan.
  if (
    warrantyUuid === `product-${holding.uuid}` ||
    (warrantyUuid === "pending-warranty" && !planned && !firstIssued)
  ) {
    return (
      firstIssued ??
      planned ??
      // No issued/pending warranty: surface the manufacturer-declared
      // coverage carried on the product record before falling back to
      // the bare placeholder. Not for a registration with none on purpose.
      (holding.coverage?.state === "no_warranty"
        ? null
        : manufacturerWarrantyRecord(holding)) ?? {
        option_display_name: "No warranty issued",
        class_name: holding.product_category || "Warranty",
        warranty_type: "PENDING_WARRANTY",
        coverage_scope: null,
        duration_in_months: null,
        is_lifetime: false,
        active_from: null,
        status: holding.status || "Active",
      }
    );
  }
  return (
    holding.warranties?.find((warranty) => warranty.uuid === warrantyUuid) ??
    (warrantyUuid === "pending-warranty" ? (planned ?? firstIssued) : null)
  );
}

/**
 * The record a registration opens on when no warranty is named in the URL,
 * matching the primary row the property-style list shows.
 */
export function defaultWarrantyUuidForHolding(holding: Holding): string {
  const firstIssued = holding.warranties?.[0]?.uuid;
  if (firstIssued) return firstIssued;
  if (plannedWarranty(holding)) return "pending-warranty";
  return `product-${holding.uuid}`;
}
